import { useEffect, useRef, useState, type ReactNode } from "react";
import { Backdrop, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Popover, Portal, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

export interface ProjectLockAccessControlProps {
  readonly isOwner: boolean;
  readonly isLocked: boolean;
  readonly ownerEmail: string | null;
  readonly onToggleLock: () => void;
  readonly isLockActionPending: boolean;
  readonly disabled?: boolean;
}

const popoverPaperSx = {
  mt: 1,
  minWidth: 220,
  borderWidth: 1,
  borderStyle: "solid",
  overflow: "hidden"
} as const;

function ProjectLockAccessControl({
  isOwner,
  isLocked,
  ownerEmail,
  onToggleLock,
  isLockActionPending,
  disabled = false
}: ProjectLockAccessControlProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const wasPendingRef = useRef(false);

  useEffect(() => {
    if (wasPendingRef.current && !isLockActionPending) {
      setConfirmOpen(false);
    }

    wasPendingRef.current = isLockActionPending;
  }, [isLockActionPending]);

  let label = "EDIT ACCESS";
  let labelColor = "text.primary";
  let icon: ReactNode = null;
  if (isOwner) {
    label = isLocked ? "LOCKED" : "UNLOCKED";
    labelColor = "text.primary";
    icon = isLocked ? <LockIcon sx={{ fontSize: 18 }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 18 }} />;
  } else if (isLocked) {
    label = "VIEW ONLY";
  }

  const tooltipTitle = isLocked ? "Only you can edit this project" : "All users can edit this project";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.currentTarget;
    setTooltipOpen(false);
    setAnchorEl((current) => (current ? null : target));
  };

  const handleOpenConfirm = () => {
    setAnchorEl(null);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    onToggleLock();
  };

  const bannerTitle = isLocked ? "Enable edit access for all users?" : "Disable edit access for everyone else?";
  const bannerBody = isLocked
    ? "Everyone will be able to edit and generate in this project."
    : "Other users can view, but only you will be able to edit or generate in this project.";
  const bannerActionLabel = isLocked ? "Enable" : "Disable";

  const button = (
    <Button
      variant="text"
      size="small"
      color="inherit"
      disabled={disabled}
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: labelColor,
        textTransform: "uppercase",
        letterSpacing: 0.46,
        fontSize: 13
      }}
    >
      {icon}
      <Box component="span" sx={{ pt: "4px" }}>
        {label}
      </Box>
      <ExpandMoreIcon sx={{ fontSize: 18 }} />
    </Button>
  );

  return (
    <>
      {isOwner ? (
        <Tooltip
          title={tooltipTitle}
          arrow
          enterDelay={200}
          open={tooltipOpen && !anchorEl}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
        >
          {button}
        </Tooltip>
      ) : (
        button
      )}
      {isOwner ? (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { elevation: 0, sx: popoverPaperSx }, list: { sx: { py: 0 } } }}
        >
          <MenuItem onClick={handleOpenConfirm} sx={{ py: 1.5 }}>
            {isLocked ? "Enable edit access for all users" : "Disable edit access for everyone else"}
          </MenuItem>
        </Menu>
      ) : (
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { elevation: 0, sx: popoverPaperSx } }}
        >
          <Typography variant="body2" sx={{ px: 2, py: 1.5, color: "text.secondary" }}>
            Owned by {ownerEmail ?? "the owner"}
          </Typography>
        </Popover>
      )}
      {confirmOpen ? (
        <Portal>
          <Backdrop
            open
            sx={{ zIndex: (theme) => theme.zIndex.modal }}
            onClick={() => {
              if (!isLockActionPending) {
                setConfirmOpen(false);
              }
            }}
          />
          <Box
            role="alertdialog"
            aria-modal="true"
            sx={{
              position: "fixed",
              top: "45%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: (theme) => theme.zIndex.modal + 1,
              width: "min(560px, calc(100vw - 32px))",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              px: 2,
              py: 1.5,
              bgcolor: "warning.main",
              color: "warning.contrastText",
              borderRadius: 1,
              boxShadow: 6
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 22, mt: 0.25, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 16 }}>
                {bannerTitle}
              </Typography>
              <Typography variant="body2">{bannerBody}</Typography>
            </Box>
            <Button
              variant="text"
              size="small"
              onClick={handleConfirm}
              disabled={isLockActionPending}
              sx={{ color: "warning.contrastText", fontWeight: 700, whiteSpace: "nowrap", minWidth: 64 }}
            >
              {isLockActionPending ? <CircularProgress size={16} sx={{ color: "warning.contrastText" }} /> : bannerActionLabel.toUpperCase()}
            </Button>
            <IconButton
              aria-label="Close"
              size="small"
              disabled={isLockActionPending}
              onClick={() => setConfirmOpen(false)}
              sx={{ color: "warning.contrastText", mt: -0.5, mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Portal>
      ) : null}
    </>
  );
}

export default ProjectLockAccessControl;
